import {Color} from '@benchling/nucleus-ui';
import {Container} from '@mantine/core';
import {useDisclosure} from '@mantine/hooks';
import reactUpdate, {type Spec} from 'immutability-helper';
import React, {memo, useRef, useCallback, useMemo} from 'react';
import {v4 as uuidv4} from 'uuid';

import * as Analytics from 'analytics/Analytics';
import {AnalyticsModalType} from 'analytics/AnalyticsTypes';
import {ANALYTICS_MODULE, AnalyticsMetadataForContext} from 'analytics/AnalyticsTypes';
import {ReactionComponent} from 'chemistry/ChemistryConstants';
import {parseReactionComponents} from 'chemistry/ChemistryDAO';
import {COMMON_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import {ActionOrigin, ActionMethod} from 'graphql-client/__generated/graphqlGlobalTypes';
import {
  ChemistryReactionRole,
  CHEMISTRY_REACTION_ROLE_TO_DISPLAY_NAME,
  CHEMISTRY_REACTION_ROLE_TO_SORT_ORDER,
} from 'notebook/integrations/chemistry/ChemistryStructuredTableConstants';
import EmptySchemePlaceholder from 'notebook/integrations/chemistry/reaction-scheme/EmptySchemePlaceholder';
import ReactionSchemeContent from 'notebook/integrations/chemistry/reaction-scheme/ReactionSchemeContent';
import ReactionSketcherModal, {
  StructureWithFormat,
} from 'notebook/integrations/chemistry/reaction-scheme/ReactionSketcherModal';
import {
  getMoleculeIdFromEvaluatedCell,
  getRoleFromEvaluatedCell,
  getColumnIndices,
} from 'notebook/integrations/chemistry/reaction-scheme/StoichiometryTableHelpers';
import useSyncReactionComponentsFromTable from 'notebook/integrations/chemistry/reaction-scheme/useSyncReactionComponentsFromTable';
import {
  StoichiometryStructuredTable,
  StoichiometryTableRow,
} from 'notebook/integrations/chemistry/StoichiometryStructuredTableAdapter';
import {StructuredTableColumn} from 'notebook/structured-tables/StructuredTableConstants';
import getBlankRow from 'notebook/structured-tables/util/getBlankRow';
import {EvaluatedTable} from 'notebook/tables/excess/Evaluator';
import {BaseTypedValueType} from 'notebook/tables/TableConstants';
import {NoteLinkedFile} from 'notebook/util/NoteUtilConstants';
import * as net from 'util/Net';
import {withLoadingScreen} from 'util/Promises';
import sortedBy from 'util/sortedBy';

interface StoichiometrySketcherProps {
  reactionSchemeFile: NoteLinkedFile;
  onUpdate: (updatedFile: NoteLinkedFile, updatedTable: StoichiometryStructuredTable) => void;
  structuredTable: StoichiometryStructuredTable;
  computedTable: EvaluatedTable;
  viewer?: boolean;
}

interface ReactionSketcherResult {
  num_reactants: number;
  num_products: number;
  num_components: number;
}

async function handleReactionSketcherSubmit(
  mrvStructure: string,
  structuredTable: StoichiometryStructuredTable,
  computedTable: EvaluatedTable,
  onUpdate: (updatedFile: NoteLinkedFile, updatedTable: StoichiometryStructuredTable) => void
): Promise<ReactionSketcherResult> {
  const {noteLinkedFile, reactants, products} = await parseReactionComponents(mrvStructure);

  const rows = mergeReactionComponentsWithExistingRows(
    reactants,
    products,
    structuredTable.rows,
    structuredTable.columns,
    computedTable
  );

  // Use the server-created NoteLinkedFile data. The server's `id` (uuid) becomes the
  // client `cid` so that the patcher can find the existing NoteLinkedFile by id on save,
  // avoiding insecure creation via the patcher path.
  const {id: serverId, ...noteLinkedFileWithoutId} = noteLinkedFile;
  const updatedFile: NoteLinkedFile = {
    ...noteLinkedFileWithoutId,
    cid: serverId,
  };
  const updatedTable = reactUpdate(structuredTable, {
    metadata: {
      $merge: {
        syncedComponents: {
          [ChemistryReactionRole.REACTANT]: reactants.map((reactant) =>
            reactant.moleculeId != null
              ? {type: 'molecule' as const, id: reactant.moleculeId}
              : {type: 'smiles' as const, smiles: reactant.canonicalSmiles}
          ),
          [ChemistryReactionRole.PRODUCT]: products.map((product) =>
            product.moleculeId != null
              ? {type: 'molecule' as const, id: product.moleculeId}
              : {type: 'smiles' as const, smiles: product.canonicalSmiles}
          ),
        },
      },
    },
    rows: {$set: rows},
  });

  onUpdate(updatedFile, updatedTable);

  return {
    num_reactants: reactants.length,
    num_products: products.length,
    num_components: reactants.length + products.length,
  };
}

function mergeReactionComponentsWithExistingRows(
  newReactants: Array<ReactionComponent>,
  newProducts: Array<ReactionComponent>,
  existingRows: Array<StoichiometryTableRow>,
  columns: Array<StructuredTableColumn>,
  computedTable: EvaluatedTable
): Array<StoichiometryTableRow> {
  const {compoundColumnIndex, roleColumnIndex} = getColumnIndices(columns);

  const makeRowKey = (identifier: string, role: ChemistryReactionRole): string => `${identifier}|${role}`;

  const existingRowsByKey: Record<string, StoichiometryTableRow> = {};
  const preservedRows: Array<StoichiometryTableRow> = [];
  const evalutedRoleByRowId: Record<string, ChemistryReactionRole> = {};

  for (let i = 0; i < existingRows.length; i++) {
    const row = existingRows[i];
    const evaluatedRow = computedTable[i];
    const moleculeId = getMoleculeIdFromEvaluatedCell(evaluatedRow[compoundColumnIndex]);
    const role = getRoleFromEvaluatedCell(evaluatedRow[roleColumnIndex]);

    if (role != null) {
      evalutedRoleByRowId[row.rowId] = role;
    }

    if (moleculeId && (role === ChemistryReactionRole.REACTANT || role === ChemistryReactionRole.PRODUCT)) {
      existingRowsByKey[makeRowKey(moleculeId, role)] = row;
    } else if (
      row.metadata?.canonicalSmiles &&
      (role === ChemistryReactionRole.REACTANT || role === ChemistryReactionRole.PRODUCT)
    ) {
      existingRowsByKey[makeRowKey(row.metadata.canonicalSmiles, role)] = row;
    } else if (moleculeId != null || role != null) {
      preservedRows.push(row);
    }
  }

  const resultRows: Array<StoichiometryTableRow> = [];
  let rowIndex = 0;

  const processComponents = (components: Array<ReactionComponent>, role: ChemistryReactionRole): void => {
    for (const component of components) {
      const rowKey =
        component.moleculeId != null
          ? makeRowKey(component.moleculeId, role)
          : makeRowKey(component.canonicalSmiles, role);
      const existingRow = existingRowsByKey[rowKey];

      if (existingRow) {
        resultRows.push(existingRow);
      } else {
        const isMoleculeNotFound = component.moleculeId == null;
        const blankCells = getBlankRow(rowIndex, columns, null);
        const compoundCellUpdate: Spec<StoichiometryTableRow['cells'][number]> = isMoleculeNotFound
          ? {
              text: {$set: component.canonicalSmiles},
              typedValue: {
                $set: {
                  type: BaseTypedValueType.STRING,
                  rawValue: component.canonicalSmiles,
                },
              },
            }
          : {
              text: {$set: component.name!},
              typedValue: {
                $set: {
                  type: BaseTypedValueType.BIOENTITY,
                  bioentitySummary: {id: component.moleculeId!, name: component.name!},
                },
              },
            };
        const cells = reactUpdate(blankCells, {
          [compoundColumnIndex]: compoundCellUpdate,
          [roleColumnIndex]: {
            text: {$set: CHEMISTRY_REACTION_ROLE_TO_DISPLAY_NAME[role]},
            typedValue: {
              $set: {
                type: BaseTypedValueType.STRING,
                rawValue: CHEMISTRY_REACTION_ROLE_TO_DISPLAY_NAME[role],
              },
            },
          },
        } satisfies Spec<StoichiometryTableRow['cells']>);
        const newRow: StoichiometryTableRow = {
          cells,
          rowId: uuidv4(),
          isFinalized: false,
          metadata: isMoleculeNotFound ? {canonicalSmiles: component.canonicalSmiles} : {},
          lastSyncedUpstreamValues: null,
        };
        resultRows.push(newRow);
        evalutedRoleByRowId[newRow.rowId] = role;
      }
      rowIndex++;
    }
  };

  processComponents(newReactants, ChemistryReactionRole.REACTANT);
  processComponents(newProducts, ChemistryReactionRole.PRODUCT);

  const outputRows = resultRows.concat(preservedRows);

  return sortedBy(outputRows, [
    (row: StoichiometryTableRow): number => {
      const role = evalutedRoleByRowId[row.rowId];
      if (role == null) {
        // Push rows without role to the end.
        return Infinity;
      } else {
        return CHEMISTRY_REACTION_ROLE_TO_SORT_ORDER[role];
      }
    },
  ]);
}

/**
 * A chemical sketcher rendered inside a Stoichiometry Structured Table. It can be used to populate the
 * table with reactants and products.
 */
function StoichiometryTableEmbeddedChemicalSketcher(props: StoichiometrySketcherProps): JSX.Element {
  // The sketcher is always expected to be rendered within an Entry so fileId should not be null
  const entryId = props.structuredTable.fileId!;
  const [isSketcherModalOpen, {open: openSketcherModal, close: closeSketcherModal}] = useDisclosure(false);
  const [error, setError] = React.useState<string | null>(null);
  const initialStructureWithFormatRef = useRef<StructureWithFormat>();
  const reactionMrvExternalFile = props.reactionSchemeFile.externalFile;
  const isSyncingFromTableToDiagram = useSyncReactionComponentsFromTable(
    props.structuredTable,
    props.computedTable,
    props.reactionSchemeFile,
    props.onUpdate
  );
  const analyticsMetadata: AnalyticsMetadataForContext = useMemo(
    () => ({
      entryId,
      fileId: entryId,
      actionOrigin: ActionOrigin.NOTEBOOK,
    }),
    [entryId]
  );

  const handleChangeChemicalStructure = useCallback(() => {
    setError(null);
  }, []);

  const openSketcherModalWithStructureAndFormat = useCallback(
    (structureWithFormat: StructureWithFormat, actionMethod: ActionMethod) => {
      initialStructureWithFormatRef.current = structureWithFormat;
      Analytics.trackEventWithModule(COMMON_ANALYTICS_ACTIONS.OPENED_MODAL, ANALYTICS_MODULE.CHEMISTRY, {
        ...analyticsMetadata,
        modalType: AnalyticsModalType.REACTION_SKETCHER,
        actionMethod,
      });
      openSketcherModal();
    },
    [openSketcherModal, analyticsMetadata]
  );

  return (
    <>
      {isSketcherModalOpen && !props.viewer && (
        <ReactionSketcherModal
          chemicalStructureError={error}
          initialStructureData={initialStructureWithFormatRef.current}
          onChangeChemicalStructure={handleChangeChemicalStructure}
          onClose={() => {
            Analytics.trackEventWithModule(
              COMMON_ANALYTICS_ACTIONS.CLOSED_MODAL,
              ANALYTICS_MODULE.CHEMISTRY,
              {
                ...analyticsMetadata,
                modalType: AnalyticsModalType.REACTION_SKETCHER,
              }
            );
            closeSketcherModal();
          }}
          onSubmit={async (newMarvinStructure: string): Promise<void> => {
            try {
              const result = await withLoadingScreen(
                handleReactionSketcherSubmit(
                  newMarvinStructure,
                  props.structuredTable,
                  props.computedTable,
                  props.onUpdate
                )
              );
              Analytics.trackEventWithModule(
                COMMON_ANALYTICS_ACTIONS.SUBMITTED_MODAL,
                ANALYTICS_MODULE.CHEMISTRY,
                {
                  ...analyticsMetadata,
                  modalType: AnalyticsModalType.REACTION_SKETCHER,
                  ...result,
                }
              );
              closeSketcherModal();
            } catch (e: unknown) {
              if (net.isJqXHR(e) && e.responseJSON?.userMessage) {
                setError(
                  'There was an error reading the structure. Make sure the structures drawn are valid'
                );
              } else {
                throw e;
              }
            }
          }}
        />
      )}

      <Container
        fluid={true}
        // Not a magic number - this is design-approved but all the rendering is flexible so this can be adjusted as needed.
        h="216px"
        p="12px"
        style={{
          backgroundColor: Color.WHITE,
          borderBottom: `1px solid ${Color.GRAY_250}`,
        }}
      >
        {reactionMrvExternalFile != null ? (
          <ReactionSchemeContent
            externalFileId={reactionMrvExternalFile.id}
            externalFileUrl={reactionMrvExternalFile.url}
            entryId={entryId}
            openSketcherModalWithStructureAndFormat={openSketcherModalWithStructureAndFormat}
            isSyncing={isSyncingFromTableToDiagram}
          />
        ) : (
          <EmptySchemePlaceholder
            openSketcherModalWithStructureAndFormat={openSketcherModalWithStructureAndFormat}
          />
        )}
      </Container>
    </>
  );
}

export default memo(StoichiometryTableEmbeddedChemicalSketcher);
